class LaserWebConfigurator extends HTMLElement {
  connectedCallback() {
    this.summary = this.querySelector('[data-laserweb-summary]');
    this.cartSummary = this.closest('.laserweb-product')?.querySelector(
      '[data-laserweb-cart-summary]',
    );
    this.proofCaption = this.querySelector(
      '.laserweb-product__render figcaption span:first-child',
    );
    this.proofStage = this.querySelector('[data-laserweb-proof-stage]');
    this.proofLength = this.querySelector('[data-laserweb-proof-length]');
    this.connectedStage = this.querySelector('[data-laserweb-connected-stage]');
    this.connectedImage = this.querySelector('[data-laserweb-connected-image]');
    this.connectedModel = this.querySelector('[data-laserweb-connected-model]');
    this.connectedStatus = this.querySelector('[data-laserweb-connected-status]');
    this.appearanceMask = this.querySelector('[data-laserweb-appearance-mask]');
    this.product = this.closest('.laserweb-product');
    this.form = this.closest('form');
    this.cartFields = {
      configurationId: this.form?.querySelector(
        '[data-laserweb-cart-configuration-id]',
      ),
      specification: this.form?.querySelector(
        '[data-laserweb-cart-specification]',
      ),
      appearance: this.form?.querySelector('[data-laserweb-cart-appearance]'),
      length: this.form?.querySelector('[data-laserweb-cart-length]'),
      linearFeet: this.form?.querySelector('[data-laserweb-cart-linear-feet]'),
      billingBasis: this.form?.querySelector(
        '[data-laserweb-cart-billing-basis]',
      ),
      strapCount: this.form?.querySelector('[data-laserweb-cart-strap-count]'),
      quantity: this.form?.querySelector('[data-laserweb-cart-quantity]'),
      buildLineId: this.form?.querySelector(
        '[data-laserweb-cart-build-line-id]',
      ),
      endA: this.form?.querySelector('[data-laserweb-cart-end-a]'),
      endB: this.form?.querySelector('[data-laserweb-cart-end-b]'),
      slotPattern: this.form?.querySelector(
        '[data-laserweb-cart-slot-pattern]',
      ),
    };
    this.product?.addEventListener('change', () => this.updateSummary());
    this.product?.addEventListener('input', () => this.updateSummary());
    this.form?.addEventListener('submit', () => this.prepareBuildLine());
    this.setupConnectedModel();
    this.updateSummary();
  }

  setupConnectedModel() {
    if (!this.connectedStage || !this.connectedModel) return;

    const showModel = () => {
      this.connectedStage.classList.add('is-model-ready');
      this.connectedStage.classList.remove('has-model-error');
      if (this.connectedStatus) {
        this.connectedStatus.textContent =
          'Interactive strap preview ready. Drag to rotate; scroll or pinch to zoom.';
      }
    };
    const showFallback = () => {
      this.connectedStage.classList.remove('is-model-ready');
      this.connectedStage.classList.add('has-model-error');
      if (this.connectedStatus) {
        this.connectedStatus.textContent =
          'Strap preview shown. The interactive view is temporarily unavailable.';
      }
    };

    this.connectedModel.addEventListener('load', showModel);
    this.connectedModel.addEventListener('error', showFallback);
    if (this.connectedModel.loaded) showModel();
  }

  value(selector, fallback) {
    return this.querySelector(selector)?.value || fallback;
  }

  hasSlots(value) {
    return value === 'Centered slot pair';
  }

  configurationId(width, feet, endA, endB) {
    const widthCode = width.replace(/[^0-9]/g, '').padStart(3, '0');
    const lengthCode = String(feet).padStart(2, '0');
    return `LW-${widthCode}-${lengthCode}-A${this.hasSlots(endA) ? 'H' : 'C'}-B${
      this.hasSlots(endB) ? 'H' : 'C'
    }`;
  }

  prepareBuildLine() {
    if (!this.cartFields.buildLineId) return;

    const random = globalThis.crypto?.getRandomValues
      ? Array.from(globalThis.crypto.getRandomValues(new Uint32Array(1)))[0]
          .toString(36)
          .toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();
    this.cartFields.buildLineId.value = `LW-${Date.now().toString(36).toUpperCase()}-${random}`;
  }

  updateSummary() {
    if (!this.product) return;

    const width = this.value('[data-laserweb-width]:checked', '1.00 in');
    const feetInput = this.querySelector('[data-laserweb-feet]');
    const requestedFeet = Number.parseInt(feetInput?.value || '4', 10);
    const feet = String(
      Math.min(50, Math.max(1, Number.isFinite(requestedFeet) ? requestedFeet : 4)),
    );
    if (feetInput && feetInput.value !== feet) feetInput.value = feet;
    const inches = Number.parseInt(feet, 10) * 12;
    const endA =
      this.product.querySelector('[data-laserweb-end-a]:checked')?.value ||
      'No slots';
    const endB =
      this.product.querySelector('[data-laserweb-end-b]:checked')?.value ||
      'No slots';
    const endAState = this.hasSlots(endA) ? 'holes' : 'clean';
    const endBState = this.hasSlots(endB) ? 'holes' : 'clean';
    const assetKey = width.replace(/[^0-9]/g, '');
    const configurationId = this.configurationId(width, feet, endA, endB);
    const slotPattern =
      endAState === 'clean' && endBState === 'clean'
        ? 'Standard trimmed ends / no slots selected'
        : 'Selected ends: 2 × 3/8 × 1/8 in slots / 1.625 in O/C';
    const screenSummary = `${width} / ${feet} ft (${inches} in) / End A: ${endA} / End B: ${endB}`;

    if (this.summary) this.summary.textContent = screenSummary;
    if (this.cartSummary) {
      this.cartSummary.textContent = `${width} / ${feet} ft (${inches} in) / ${
        endAState === 'holes' ? 'slotted' : 'no-slot'
      } End A / ${endBState === 'holes' ? 'slotted' : 'no-slot'} End B`;
    }

    if (this.proofStage) {
      this.proofStage.dataset.laserwebWidthValue = assetKey;
      this.proofStage.dataset.laserwebLengthValue = String(inches);
      this.proofStage.dataset.laserwebEndAState = endAState;
      this.proofStage.dataset.laserwebEndBState = endBState;
      this.proofStage.setAttribute(
        'aria-label',
        `LASERWEB configuration proof: ${width} wide, ${feet} feet (${inches} inches) cut to length, both ends permanently trimmed at 60 degrees with 1/4-inch corner radii, End A ${
          endAState === 'holes' ? 'with the centered slot pair' : 'without slots'
        }, End B ${
          endBState === 'holes' ? 'with the centered slot pair' : 'without slots'
        }. Slot pairs are 3/8 by 1/8 inches at 1.625 inches on center.`,
      );
    }
    if (this.proofLength) this.proofLength.textContent = `${feet} FT / ${inches} IN`;

    if (this.connectedStage) {
      // Numeric width codes remain hyphenated in HTMLElement.dataset
      // (for example, data-laserweb-model-075 becomes dataset['laserwebModel-075']).
      // Reading the explicit attributes keeps all three source models addressable.
      const beauty = this.connectedStage.getAttribute(
        `data-laserweb-beauty-${assetKey}`,
      );
      const mask = this.connectedStage.getAttribute(
        `data-laserweb-mask-${assetKey}`,
      );
      const model = this.connectedStage.getAttribute(
        `data-laserweb-model-${assetKey}`,
      );
      if (beauty && this.connectedImage && this.connectedImage.src !== beauty) {
        this.connectedImage.src = beauty;
      }
      if (beauty && this.connectedModel) this.connectedModel.setAttribute('poster', beauty);
      if (model && this.connectedModel && this.connectedModel.getAttribute('src') !== model) {
        this.connectedStage.classList.remove('is-model-ready');
        if (this.connectedStatus) {
          this.connectedStatus.textContent = 'Loading the selected strap preview.';
        }
        this.connectedModel.setAttribute('src', model);
      }
      if (mask && this.appearanceMask) {
        this.appearanceMask.style.setProperty('--laserweb-mask-image', `url("${mask}")`);
      }
      this.connectedStage.dataset.laserwebWidthValue = assetKey;
    }

    if (this.proofCaption) {
      this.proofCaption.textContent = `${width.toUpperCase()} WIDE × .0625 IN THICK / END TREATMENT PREVIEW`;
    }

    if (this.cartFields.configurationId) this.cartFields.configurationId.value = configurationId;
    if (this.cartFields.specification) {
      this.cartFields.specification.value = `Black woven webbing / ${width} wide × .0625 in thick`;
    }
    if (this.cartFields.appearance) {
      this.cartFields.appearance.value = 'Black woven / catalog mask ready';
    }
    if (this.cartFields.length) this.cartFields.length.value = `${feet} ft / ${inches} in`;
    if (this.cartFields.linearFeet) this.cartFields.linearFeet.value = `${feet} ft`;
    if (this.cartFields.billingBasis) {
      this.cartFields.billingBasis.value =
        'Per linear foot / cart quantity represents linear feet';
    }
    if (this.cartFields.strapCount) this.cartFields.strapCount.value = '1';
    if (this.cartFields.quantity) this.cartFields.quantity.value = feet;
    if (this.cartFields.endA) {
      this.cartFields.endA.value =
        endAState === 'holes'
          ? '60 degree trim / 1/4 in corner radii / centered 3/8 × 1/8 in slot pair / 1.625 in O/C'
          : '60 degree trim / 1/4 in corner radii / no slots';
    }
    if (this.cartFields.endB) {
      this.cartFields.endB.value =
        endBState === 'holes'
          ? '60 degree trim / 1/4 in corner radii / centered 3/8 × 1/8 in slot pair / 1.625 in O/C'
          : '60 degree trim / 1/4 in corner radii / no slots';
    }
    if (this.cartFields.slotPattern) this.cartFields.slotPattern.value = slotPattern;
  }
}

if (!customElements.get('laserweb-configurator')) {
  customElements.define('laserweb-configurator', LaserWebConfigurator);
}
